/**
 * Feature Flags Configuration
 *
 * Controls which modules and features are visible in the UI.
 * Module code stays in the repo as reference implementations —
 * these flags just hide them from the sidebar and settings.
 *
 * When forking this starter:
 * - Set VITE_FEATURE_[NAME]=false in .dev.vars to hide modules you don't need
 * - The module code remains available as patterns for Claude Code to reference
 *
 * @see src/shared/config/nav.ts for sidebar item filtering
 */

const isEnabled = (envVar: string): boolean => {
  const value = import.meta.env[envVar]
  return value !== 'false'
}

/** Ambient Notes: inherited starter modules are OFF by default.
 *  Opt back in with VITE_FEATURE_<NAME>=true for development/exploration. */
const isEnabledOptIn = (envVar: string): boolean => {
  return import.meta.env[envVar] === 'true'
}

const isDev = import.meta.env['DEV'] === true

export const features = {
  // ── Module Visibility ──────────────────────────────────────────────────
  // Ambient Notes is an audio capture app — the starter's general-purpose
  // modules (chat, files, skills, etc.) stay in the codebase as reference
  // patterns but are hidden from the UI. Flip VITE_FEATURE_<NAME>=true to
  // surface them during development.

  /** AI Chat + Extract pages (starter) */
  chat: isEnabledOptIn('VITE_FEATURE_CHAT'),

  /** File upload/management (starter) */
  files: isEnabledOptIn('VITE_FEATURE_FILES'),

  /** Activity audit log (starter) */
  activity: isEnabledOptIn('VITE_FEATURE_ACTIVITY'),

  /** In-app notifications bell (starter) */
  notifications: isEnabledOptIn('VITE_FEATURE_NOTIFICATIONS'),

  /** API token management in settings (starter) */
  apiTokens: isEnabledOptIn('VITE_FEATURE_API_TOKENS'),

  /** Skills dashboard + slash-command activation (starter) */
  skills: isEnabledOptIn('VITE_FEATURE_SKILLS'),

  /** MCP Connectors (starter) */
  connectors: isEnabledOptIn('VITE_FEATURE_CONNECTORS'),

  // ── UI Features ────────────────────────────────────────────────────────

  /** Theme/colour picker in preferences */
  themePicker: isEnabled('VITE_FEATURE_THEME_PICKER'),

  // ── Dev Tools ──────────────────────────────────────────────────────────
  // Shown in dev mode by default. Set explicitly to show in production.

  /** Master toggle for dev tool pages */
  devTools: import.meta.env['VITE_FEATURE_DEV_TOOLS'] === 'true' || isDev,

  /** Style guide page */
  styleGuide: import.meta.env['VITE_FEATURE_STYLE_GUIDE'] === 'true' || isDev,

  /** Components showcase page */
  components: isEnabled('VITE_FEATURE_COMPONENTS'),
} as const

export type Features = typeof features
